import React, { useState, useEffect } from 'react';
import type { User } from '@supabase/supabase-js';
import type { CurrentPage } from '../App';
import { ArrowLeft, LogOut, User as UserIcon, Award, Save, Edit3, Settings, Home, Mail, Building2, Shield, CheckCircle, XCircle, CreditCard, Crown, Check, Trash2, AlertTriangle, ChevronDown, ExternalLink, MessageCircle } from 'lucide-react';
import { useSubscription } from '../hooks/useSubscription';
import { supabase } from '../lib/supabaseClient';
import { getPlanById, formatPrice } from '../types/pricing';
import { deleteUserAccount } from '../services/accountService';
import ContactUsModal from './ContactUsModal';
import StorageUsageDisplay from './StorageUsageDisplay';
import SubscriptionManagementCompact from './SubscriptionManagementCompact';

// Define the props for SettingsPage
export interface SettingsPageProps {
  currentUser: User | null;
  onNavigate: (page: CurrentPage) => void;
  handleLogout: () => Promise<void>; // Changed from onLogout
}

interface AssessorDetails {
  assessorName: string;
  assessorNumber: string;
  assessorCompany: string;
  assessorType: string;
  certificationScheme: string;
}

const SettingsPage: React.FC<SettingsPageProps> = ({ currentUser, onNavigate, handleLogout }) => { // Use handleLogout
  // Scroll to top when page loads (backup - App.tsx also handles this)
  React.useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const { planType, isLoading: loadingSubscription } = useSubscription();

  // Profile data state for real-time account type display
  const [profileData, setProfileData] = useState<any>(null);
  const [loadingProfile, setLoadingProfile] = useState(true);

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  
  // Account deletion state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isDangerZoneExpanded, setIsDangerZoneExpanded] = useState(false);
  
  // Contact Us modal state
  const [showContactModal, setShowContactModal] = useState(false);
  
  const email = currentUser?.email || 'No email found';
  
  // Initialize assessor details empty - will be populated from profiles table only
  const [assessorDetails, setAssessorDetails] = useState<AssessorDetails>({
    assessorName: '',
    assessorNumber: '',
    assessorCompany: '',
    assessorType: 'Energy Assessor',
    certificationScheme: ''
  });

  // Fetch profile data from profiles table for real-time account type display AND assessor details
  useEffect(() => {
    const fetchProfileData = async () => {
      if (!currentUser?.id) return;
      
      setLoadingProfile(true);
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('account_type, subscription_type, subscription_plan, assessor_id, assessor_type, company_name, first_name, last_name, certification_scheme')
          .eq('id', currentUser.id)
          .single();
        
        if (error) {
          console.error('[Settings] Error fetching profile:', error);
        } else {
          setProfileData(data);
          console.log('[Settings] Profile data loaded from DB:', data);
          
          // Update assessor details from profiles table ONLY
          if (data) {
            setAssessorDetails({
              assessorName: `${data.first_name || ''} ${data.last_name || ''}`.trim(),
              assessorNumber: data.assessor_id || '',
              assessorCompany: data.company_name || '',
              assessorType: data.assessor_type || 'Energy Assessor',
              certificationScheme: data.certification_scheme || ''
            });
            console.log('[Settings] ✅ Assessor details loaded from profiles table:', {
              name: `${data.first_name} ${data.last_name}`,
              assessor_id: data.assessor_id,
              company: data.company_name,
              type: data.assessor_type
            });
          }
        }
      } catch (err) {
        console.error('[Settings] Error fetching profile:', err);
      } finally {
        setLoadingProfile(false);
      }
    };
    
    fetchProfileData();
  }, [currentUser?.id]);

  const handleAssessorDetailChange = (field: keyof AssessorDetails, value: string) => {
    setAssessorDetails(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const saveAssessorDetails = async () => {
    if (!currentUser) return;
    
    setIsSaving(true);
    setSaveMessage(null);
    
    try {
      // Parse name into first and last
      const nameParts = assessorDetails.assessorName.split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          assessor_id: assessorDetails.assessorNumber,
          assessor_type: assessorDetails.assessorType,
          company_name: assessorDetails.assessorCompany,
          first_name: firstName,
          last_name: lastName,
          certification_scheme: assessorDetails.certificationScheme,
        })
        .eq('id', currentUser.id);

      if (profileError) {
        console.error('[Settings] Error updating profile:', profileError);
        throw profileError;
      }

      console.log('[Settings] ✅ Assessor details saved to profiles table:', {
        assessor_id: assessorDetails.assessorNumber,
        assessor_type: assessorDetails.assessorType,
        company_name: assessorDetails.assessorCompany,
        first_name: firstName,
        last_name: lastName
      });
      // Refresh profile data to show updated values
      const { data: updatedProfile } = await supabase
        .from('profiles')
        .select('account_type, subscription_type, subscription_plan, assessor_id, assessor_type, company_name, first_name, last_name')
        .eq('id', currentUser.id)
        .single();
      
      if (updatedProfile) {
        setProfileData(updatedProfile);
        console.log('[Settings] Profile data refreshed after save:', updatedProfile);
      }
      
      setSaveMessage('Assessor details saved successfully!');
      setIsEditing(false);
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSaveMessage(null);
      }, 3000);
      
    } catch (error) {
      console.error('Error saving assessor details:', error);
      setSaveMessage('Failed to save assessor details. Please try again.');
      
      // Clear error message after 5 seconds
      setTimeout(() => {
        setSaveMessage(null);
      }, 5000);
    } finally {
      setIsSaving(false);
    }
  };

  const assessorTypeOptions = [
    "Energy Assessor",
    "Property Inventory Clerk", 
    "Chartered Surveyor",
    "Valuer",
    "Retrofit Assessor",
    "Other"
  ];

  const certificationSchemeOptions = [
    "Stroma",
    "Elmhurst Energy",
    "Quidos",
    "NHER",
    "ECMK",
    "Bre",
    "CIBSE",
    "Other"
  ];

  const handleDeleteAccount = async () => {
    if (!currentUser) return;
    
    // Require exact confirmation text
    if (deleteConfirmText !== 'DELETE MY ACCOUNT') {
      setDeleteError('Please type "DELETE MY ACCOUNT" to confirm');
      return;
    }

    setIsDeleting(true);
    setDeleteError(null);

    try {
      console.log('[Settings] Starting account deletion...');
      
      const result = await deleteUserAccount(currentUser.id);
      
      if (!result.success) {
        setDeleteError(result.error || 'Failed to delete account');
        setIsDeleting(false);
        return;
      }

      console.log('[Settings] Account deletion successful, logging out...');
      
      // Account deleted successfully, log out
      await handleLogout();
      
    } catch (error: any) {
      console.error('[Settings] Error during account deletion:', error);
      setDeleteError(error.message || 'An unexpected error occurred');
      setIsDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50 flex flex-col">
      {/* Modern Gradient Header */}
      <header className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 shadow-2xl sticky top-0 z-40">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/0 via-white/10 to-purple-600/0"></div>
        <div className="relative w-full px-4 sm:px-6 flex items-center justify-between app-header-padding">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
              <Settings className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-white">
                Settings
              </h1>
              <p className="text-blue-100 text-sm hidden sm:block">Manage your account and preferences</p>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <button
              onClick={() => onNavigate({ page: 'start' })}
              className="p-2.5 bg-white/10 hover:bg-white/20 rounded-xl transition-all duration-200 backdrop-blur-sm border border-white/20 hover:scale-110"
              title="Home"
            >
              <Home className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 p-4 sm:p-6 lg:p-8 pb-20">
         <div className="max-w-3xl mx-auto space-y-6">
            {/* Profile Section */}
            <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-xl border border-white/50 hover:shadow-2xl transition-all duration-300">
               <div className="flex items-center mb-6">
                  <div className="p-3 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl shadow-lg">
                     <UserIcon className="text-white" size={24} />
                  </div>
                  <h2 className="text-2xl font-bold ml-4 bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                     Profile Information
                  </h2>
               </div>
               <div className="space-y-4">
                  <div className="flex items-center space-x-3 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
                     <UserIcon className="text-blue-600" size={18} />
                     <div>
                        <p className="text-xs font-medium text-gray-500">Full Name</p>
                        <p className="text-gray-900 font-semibold">{profileData?.first_name || ''} {profileData?.last_name || ''}</p>
                     </div>
                  </div>
                  <div className="flex items-center space-x-3 p-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-100">
                     <Mail className="text-purple-600" size={18} />
                     <div>
                        <p className="text-xs font-medium text-gray-500">Email Address</p>
                        <p className="text-gray-900 font-semibold">{email}</p>
                     </div>
                  </div>
               </div>
            </div>

            {/* Contact Us Section */}
            <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-xl border border-white/50 hover:shadow-2xl transition-all duration-300">
               <div className="flex items-center mb-4">
                  <div className="p-3 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl shadow-lg">
                     <MessageCircle className="text-white" size={24} />
                  </div>
                  <h2 className="text-2xl font-bold ml-4 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                     Contact Us
                  </h2>
               </div>
               
               <p className="text-sm text-gray-600 mb-4">
                  Have a question, suggestion, or found a bug? We're here to help!
               </p>
               
               <button
                  onClick={() => setShowContactModal(true)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl"
               >
                  <MessageCircle size={18} />
                  Get in Touch
               </button>
            </div>

            {/* Account Type Section */}
            <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-xl border border-white/50 hover:shadow-2xl transition-all duration-300">
               <div className="flex items-center mb-6">
                  <div className={`p-3 rounded-xl shadow-lg ${
                     (profileData?.account_type || currentUser?.user_metadata?.account_type) === 'business' 
                        ? 'bg-gradient-to-r from-purple-500 to-indigo-600' 
                        : 'bg-gradient-to-r from-blue-500 to-cyan-600'
                  }`}>
                     {(profileData?.account_type || currentUser?.user_metadata?.account_type) === 'business' ? (
                        <Building2 className="text-white" size={24} />
                     ) : (
                        <UserIcon className="text-white" size={24} />
                     )}
                  </div>
                  <h2 className={`text-2xl font-bold ml-4 bg-clip-text text-transparent ${
                     (profileData?.account_type || currentUser?.user_metadata?.account_type) === 'business'
                        ? 'bg-gradient-to-r from-purple-600 to-indigo-600'
                        : 'bg-gradient-to-r from-blue-600 to-cyan-600'
                  }`}>
                     Account Type
                  </h2>
               </div>
               {loadingProfile ? (
                  <div className="flex items-center justify-center py-8">
                     <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
               ) : (
                  <div className="space-y-4">
                     <div className={`flex items-center space-x-3 p-4 rounded-xl border ${
                        (profileData?.account_type || currentUser?.user_metadata?.account_type) === 'business'
                           ? 'bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200'
                           : 'bg-gradient-to-r from-blue-50 to-cyan-50 border-blue-200'
                     }`}>
                        <div className="flex-1">
                           <p className="text-xs font-medium text-gray-500 mb-1">Account Level</p>
                           <p className="text-lg font-bold text-gray-900 flex items-center gap-2">
                              {(profileData?.account_type || currentUser?.user_metadata?.account_type) === 'business' ? (
                                 <>
                                    <Building2 className="text-purple-600" size={20} />
                                    Business Account
                                 </>
                              ) : (
                                 <>
                                    <UserIcon className="text-blue-600" size={20} />
                                    Personal Account
                                 </>
                              )}
                           </p>
                           <p className="text-xs text-gray-600 mt-1">
                              {(profileData?.account_type || currentUser?.user_metadata?.account_type) === 'business' 
                                 ? 'For companies and organizations' 
                                 : 'For individual assessors and freelancers'}
                           </p>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                           <div className={`px-3 py-1.5 rounded-full text-xs font-bold ${
                              (profileData?.account_type || currentUser?.user_metadata?.account_type) === 'business'
                                 ? 'bg-purple-600 text-white'
                                 : 'bg-blue-600 text-white'
                           }`}>
                              {(profileData?.account_type || currentUser?.user_metadata?.account_type) === 'business' ? 'BUSINESS' : 'PERSONAL'}
                           </div>
                           {(profileData?.subscription_plan || planType) && (
                              <div className={`px-3 py-1.5 rounded-full text-xs font-bold ${
                                 (profileData?.subscription_plan || planType) === 'standard' ? 'bg-gray-500 text-white' :
                                 (profileData?.subscription_plan || planType) === 'pro_trial' ? 'bg-amber-500 text-white' :
                                 'bg-gradient-to-r from-blue-600 to-purple-600 text-white'
                              }`}>
                                 {(profileData?.subscription_plan || planType) === 'pro_trial' ? 'PRO TRIAL' : (profileData?.subscription_plan || planType).toUpperCase()}
                              </div>
                           )}
                        </div>
                     </div>
                  </div>
               )}
                  
               {/* Business Account Details */}
               {currentUser?.user_metadata?.account_type === 'business' && (
                  <div className="space-y-3 pt-2 border-t border-gray-200">
                     {currentUser?.user_metadata?.company_name && (
                        <div className="flex items-center space-x-3 p-3 bg-purple-50/50 rounded-lg">
                           <Building2 className="text-purple-600" size={16} />
                           <div>
                              <p className="text-xs font-medium text-gray-500">Company Name</p>
                              <p className="text-gray-900 font-semibold">{currentUser.user_metadata.company_name}</p>
                           </div>
                        </div>
                     )}
                     
                     {currentUser?.user_metadata?.company_registration && (
                        <div className="flex items-center space-x-3 p-3 bg-indigo-50/50 rounded-lg">
                           <Shield className="text-indigo-600" size={16} />
                           <div>
                              <p className="text-xs font-medium text-gray-500">Company Registration</p>
                              <p className="text-gray-900 font-semibold">{currentUser.user_metadata.company_registration}</p>
                           </div>
                        </div>
                     )}
                     
                     {currentUser?.user_metadata?.vat_number && (
                        <div className="flex items-center space-x-3 p-3 bg-purple-50/50 rounded-lg">
                           <Shield className="text-purple-600" size={16} />
                           <div>
                              <p className="text-xs font-medium text-gray-500">VAT Number</p>
                              <p className="text-gray-900 font-semibold">{currentUser.user_metadata.vat_number}</p>
                           </div>
                        </div>
                     )}
                  </div>
               )}
            </div>

            {/* Apple IAP Subscription Management */}
            <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-xl border border-white/50 hover:shadow-2xl transition-all duration-300">
               <div className="flex items-center mb-6">
                  <div className="p-3 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl shadow-lg">
                     <CreditCard className="text-white" size={24} />
                  </div>
                  <h2 className="text-2xl font-bold ml-4 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                     Subscription Management
                  </h2>
               </div>
               
               <SubscriptionManagementCompact currentUser={currentUser} />
            </div>


            {/* Cloud Storage Usage Section */}
            <StorageUsageDisplay />

            {/* Assessor Details Section */}
            <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-xl border border-white/50 hover:shadow-2xl transition-all duration-300">
               <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center">
                     <div className="p-3 bg-gradient-to-r from-yellow-500 to-orange-600 rounded-xl shadow-lg">
                        <Award className="text-white" size={24} />
                     </div>
                     <h2 className="text-2xl font-bold ml-4 bg-gradient-to-r from-yellow-600 to-orange-600 bg-clip-text text-transparent">
                        Assessor Details
                     </h2>
                  </div>
                  <button
                     onClick={() => isEditing ? saveAssessorDetails() : setIsEditing(true)}
                     disabled={isSaving}
                     className={`flex items-center space-x-2 px-6 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105 ${
                        isEditing 
                           ? 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white ring-2 ring-green-200/50' 
                           : 'bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-700 hover:to-orange-700 text-white ring-2 ring-yellow-200/50'
                     } ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                     {isEditing ? (
                        <>
                           <Save size={18} />
                           <span>{isSaving ? 'Saving...' : 'Save Changes'}</span>
                        </>
                     ) : (
                        <>
                           <Edit3 size={18} />
                           <span>Edit Details</span>
                        </>
                     )}
                  </button>
               </div>

               {saveMessage && (
                  <div className={`mb-6 p-4 rounded-xl flex items-center space-x-3 animate-in fade-in duration-200 ${
                     saveMessage.includes('success') 
                        ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200' 
                        : 'bg-gradient-to-r from-red-50 to-rose-50 border-2 border-red-200'
                  }`}>
                     {saveMessage.includes('success') ? (
                        <CheckCircle className="text-green-600" size={20} />
                     ) : (
                        <XCircle className="text-red-600" size={20} />
                     )}
                     <p className={`font-medium ${
                        saveMessage.includes('success') ? 'text-green-800' : 'text-red-800'
                     }`}>
                        {saveMessage}
                     </p>
                  </div>
               )}

               <div className="space-y-4">
                  <div>
                     <label className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name
                     </label>
                     {isEditing ? (
                        <input
                           type="text"
                           value={assessorDetails.assessorName}
                           onChange={(e) => handleAssessorDetailChange('assessorName', e.target.value)}
                           className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all bg-white"
                           placeholder="Enter certified assessor name"
                        />
                     ) : (
                        <p className="text-gray-900 bg-gradient-to-r from-gray-50 to-slate-50 px-4 py-3 rounded-xl font-medium border border-gray-200">
                           {assessorDetails.assessorName || 'Not set'}
                        </p>
                     )}
                  </div>

                  <div>
                     <label className="block text-sm font-medium text-gray-700 mb-2">
                        DEA Registration Number
                     </label>
                     {isEditing ? (
                        <input
                           type="text"
                           value={assessorDetails.assessorNumber}
                           onChange={(e) => handleAssessorDetailChange('assessorNumber', e.target.value)}
                           className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all bg-white"
                           placeholder="e.g., STRO123456"
                        />
                     ) : (
                        <p className="text-gray-900 bg-gradient-to-r from-gray-50 to-slate-50 px-4 py-3 rounded-xl font-medium border border-gray-200">
                           {assessorDetails.assessorNumber || 'Not set'}
                        </p>
                     )}
                  </div>

                  <div>
                     <label className="block text-sm font-medium text-gray-700 mb-2">
                        Company/Organization
                     </label>
                     {isEditing ? (
                        <input
                           type="text"
                           value={assessorDetails.assessorCompany}
                           onChange={(e) => handleAssessorDetailChange('assessorCompany', e.target.value)}
                           className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all bg-white"
                           placeholder="Energy assessment company"
                        />
                     ) : (
                        <p className="text-gray-900 bg-gradient-to-r from-gray-50 to-slate-50 px-4 py-3 rounded-xl font-medium border border-gray-200">
                           {assessorDetails.assessorCompany || 'Not set'}
                        </p>
                     )}
                  </div>

                  <div>
                     <label className="block text-sm font-medium text-gray-700 mb-2">
                        Assessor Type
                     </label>
                     {isEditing ? (
                        <select
                           value={assessorDetails.assessorType}
                           onChange={(e) => handleAssessorDetailChange('assessorType', e.target.value)}
                           className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all bg-white"
                        >
                           {assessorTypeOptions.map(type => (
                              <option key={type} value={type}>{type}</option>
                           ))}
                        </select>
                     ) : (
                        <p className="text-gray-900 bg-gradient-to-r from-gray-50 to-slate-50 px-4 py-3 rounded-xl font-medium border border-gray-200">
                           {assessorDetails.assessorType || 'Not set'}
                        </p>
                     )}
                  </div>

                  <div>
                     <label className="block text-sm font-medium text-gray-700 mb-2">
                        Certification Scheme
                     </label>
                     {isEditing ? (
                        <select
                           value={assessorDetails.certificationScheme}
                           onChange={(e) => handleAssessorDetailChange('certificationScheme', e.target.value)}
                           className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all bg-white"
                        >
                           <option value="">Select certification scheme</option>
                           {certificationSchemeOptions.map(scheme => (
                              <option key={scheme} value={scheme}>{scheme}</option>
                           ))}
                        </select>
                     ) : (
                        <p className="text-gray-900 bg-gradient-to-r from-gray-50 to-slate-50 px-4 py-3 rounded-xl font-medium border border-gray-200">
                           {assessorDetails.certificationScheme || 'Not set'}
                        </p>
                     )}
                  </div>
               </div>

               <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl flex items-start space-x-3">
                  <Shield className="text-blue-600 flex-shrink-0" size={20} />
                  <div>
                     <p className="text-sm font-semibold text-blue-900 mb-1">Auto-Fill Feature</p>
                     <p className="text-sm text-blue-700">
                        These details will automatically populate in EPC forms to save time during assessments.
                     </p>
                  </div>
               </div>
            </div>

            {/* Danger Zone - Delete Account (Collapsible) */}
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border-2 border-red-200 hover:shadow-2xl transition-all duration-300">
               <button
                  onClick={() => setIsDangerZoneExpanded(!isDangerZoneExpanded)}
                  className="w-full p-6 flex items-center justify-between hover:bg-red-50/50 transition-colors rounded-2xl"
               >
                  <div className="flex items-center">
                     <div className="p-3 bg-gradient-to-r from-red-500 to-rose-600 rounded-xl shadow-lg">
                        <AlertTriangle className="text-white" size={24} />
                     </div>
                     <h2 className="text-2xl font-bold ml-4 bg-gradient-to-r from-red-600 to-rose-600 bg-clip-text text-transparent">
                        Account Deletion
                     </h2>
                  </div>
                  <ChevronDown 
                     className={`w-6 h-6 text-red-600 transition-transform duration-300 ${isDangerZoneExpanded ? 'rotate-180' : ''}`} 
                  />
               </button>
               
               {isDangerZoneExpanded && (
                  <div className="px-6 pb-6 space-y-4">
                     <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 mb-4">
                        <p className="text-sm text-blue-800 font-medium mb-2">
                           💳 Cancel Subscription First
                        </p>
                        <p className="text-xs text-blue-700 mb-2">
                           Before deleting your account, please cancel your subscription through Apple to avoid future charges.
                        </p>
                        <a 
                           href="https://apps.apple.com/account/subscriptions" 
                           target="_blank" 
                           rel="noopener noreferrer" 
                           className="inline-flex items-center px-4 py-2 bg-black text-white text-xs font-semibold rounded-lg hover:bg-gray-800 transition-colors"
                        >
                           <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M18.71,19.5C17.88,20.74 17,21.95 15.66,21.97C14.32,22 13.89,21.18 12.37,21.18C10.84,21.18 10.37,21.95 9.1,22C7.79,22.05 6.8,20.68 5.96,19.47C4.25,17 2.94,12.45 4.7,9.39C5.57,7.87 7.13,6.91 8.82,6.88C10.1,6.86 11.32,7.75 12.11,7.75C12.89,7.75 14.37,6.68 15.92,6.84C16.57,6.87 18.39,7.1 19.56,8.82C19.47,8.88 17.39,10.1 17.41,12.63C17.44,15.65 20.06,16.66 20.09,16.67C20.06,16.74 19.67,18.11 18.71,19.5M13,3.5C13.73,2.67 14.94,2.04 15.94,2C16.07,3.17 15.6,4.35 14.9,5.19C14.21,6.04 13.07,6.7 11.95,6.61C11.8,5.46 12.36,4.26 13,3.5Z"/>
                           </svg>
                           Manage Apple Subscriptions
                        </a>
                     </div>
                     
                     <div className="bg-gradient-to-r from-red-50 to-rose-50 border-2 border-red-200 rounded-xl p-4">
                        <p className="text-sm text-red-800 font-medium mb-2">
                           ⚠️ Delete Your Account
                        </p>
                        <p className="text-xs text-red-700">
                           This action is <strong>permanent and cannot be undone</strong>. All your data will be deleted, including:
                        </p>
                        <ul className="text-xs text-red-700 mt-2 ml-4 space-y-1 list-disc">
                           <li>All properties and surveys</li>
                           <li>All uploaded images and files</li>
                           <li>All team data (if you're an owner)</li>
                           <li>Your account and profile</li>
                        </ul>
                     </div>

                     <button
                        onClick={() => setShowDeleteModal(true)}
                        className="w-full flex items-center justify-center px-6 py-3.5 bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 rounded-xl text-white font-semibold shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105 ring-2 ring-red-200/50"
                     >
                        <Trash2 size={20} className="mr-3" />
                        <span>Delete Account</span>
                     </button>
                  </div>
               )}
            </div>

            {/* Logout Button */}
             <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-xl border border-white/50 hover:shadow-2xl transition-all duration-300">
                <button
                   onClick={handleLogout}
                   className="w-full flex items-center justify-center px-6 py-3.5 bg-gradient-to-r from-slate-600 to-slate-700 hover:from-slate-700 hover:to-slate-800 rounded-xl text-white font-semibold shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105"
                 >
                   <LogOut size={20} className="mr-3" />
                   <span>Log Out</span>
                 </button>
             </div>
         </div>
      </main>

      {/* Delete Account Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 border-2 border-red-200">
            {/* Header */}
            <div className="flex items-center mb-4">
              <div className="p-3 bg-gradient-to-r from-red-500 to-rose-600 rounded-xl">
                <AlertTriangle className="text-white" size={24} />
              </div>
              <h3 className="text-2xl font-bold ml-3 text-red-600">
                Delete Account?
              </h3>
            </div>

            {/* Warning */}
            <div className="bg-gradient-to-r from-red-50 to-rose-50 border-2 border-red-200 rounded-xl p-4 mb-4">
              <p className="text-sm font-bold text-red-900 mb-2">
                ⚠️ This action is permanent!
              </p>
              <p className="text-sm text-red-800 mb-2">
                Your account and <strong>all associated data</strong> will be permanently deleted:
              </p>
              <ul className="text-sm text-red-700 ml-4 space-y-1 list-disc">
                <li>All properties and surveys</li>
                <li>All uploaded files and images</li>
                <li>Team data (if you're an owner)</li>
                <li>All settings and preferences</li>
              </ul>
              <p className="text-sm font-bold text-red-900 mt-3">
                This cannot be undone!
              </p>
            </div>

            {/* Confirmation Input */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Type <span className="font-mono bg-red-100 px-2 py-0.5 rounded text-red-700">DELETE MY ACCOUNT</span> to confirm:
              </label>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => {
                  setDeleteConfirmText(e.target.value);
                  setDeleteError(null);
                }}
                placeholder="DELETE MY ACCOUNT"
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
                disabled={isDeleting}
              />
            </div>

            {/* Error Message */}
            {deleteError && (
              <div className="mb-4 p-3 bg-red-100 border-2 border-red-300 rounded-xl flex items-start gap-2">
                <XCircle className="text-red-600 flex-shrink-0 mt-0.5" size={18} />
                <p className="text-sm text-red-800 font-medium">{deleteError}</p>
              </div>
            )}

            {/* Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteConfirmText('');
                  setDeleteError(null);
                }}
                disabled={isDeleting}
                className="flex-1 px-4 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={isDeleting || deleteConfirmText !== 'DELETE MY ACCOUNT'}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 text-white font-semibold rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isDeleting ? (
                  <>
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Deleting...</span>
                  </>
                ) : (
                  <>
                    <Trash2 size={18} />
                    <span>Delete Forever</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Contact Us Modal */}
      <ContactUsModal
        isOpen={showContactModal}
        onClose={() => setShowContactModal(false)}
        userEmail={email}
      />
    </div>
  );
};

export default SettingsPage; 